import type { Task } from '@dyspatch/core';

/**
 * Where a task is filed once the fleet is done with it, in a module of its own.
 *
 * The Tasks page's five groups live here, and so does the 24-hour cut-off the
 * Inbox's failures panel draws, because they are the same rule seen from two
 * pages: what the panel stops showing is exactly what the `Closed` group starts
 * showing, and a count on one that disagrees with the list on the other is the
 * bug this module exists to make impossible.
 *
 * Same arrangement as `presence.ts`: `fleet.ts` re-exports these so views keep
 * one import, and they live here because a module with no `@/` imports is one a
 * test can reach - and what a person can and cannot find is exactly the kind of
 * rule that should have a test.
 */

/**
 * How far back the Tasks page's history reaches, and what the control says.
 *
 * `today` is the calendar day in the reader's timezone, which is what the page
 * meant by "Done today" before this and is what a person means by today at
 * 00:30. The other two are rolling windows, because "the last week" is a week
 * back from now rather than back to a Monday.
 */
export const TASK_RANGES = [
  { value: 'today', label: 'Today', short: 'Today' },
  { value: '7d', label: '7 days', short: '7d' },
  { value: '30d', label: '30 days', short: '30d' },
] as const satisfies readonly { value: string; label: string; short: string }[];

export type TaskRange = (typeof TASK_RANGES)[number]['value'];

export const DEFAULT_TASK_RANGE: TaskRange = '7d';

export function isTaskRange(value: unknown): value is TaskRange {
  return TASK_RANGES.some((range) => range.value === value);
}

/**
 * The status filter, as sets of real task statuses.
 *
 * The names are the ones the groups already use, because a person filtering to
 * "Waiting" and getting the group called "Waiting on a human" is the whole
 * contract. `closed` is `done` and `cancelled`: a failure is not closed, it is
 * waiting for somebody to decide about it, which is why it has a filter of its
 * own.
 */
export const TASK_STATUS_FILTERS = [
  { value: 'all', label: 'All', statuses: null },
  { value: 'gate', label: 'Waiting', statuses: ['review'] },
  { value: 'running', label: 'Running', statuses: ['running', 'reporting'] },
  { value: 'queued', label: 'Queued', statuses: ['queued', 'offered', 'claimed', 'draft'] },
  { value: 'failed', label: 'Failed', statuses: ['failed'] },
  { value: 'closed', label: 'Closed', statuses: ['done', 'cancelled'] },
] as const satisfies readonly {
  value: string;
  label: string;
  statuses: readonly Task['status'][] | null;
}[];

export type TaskStatusFilter = (typeof TASK_STATUS_FILTERS)[number]['value'];

export const DEFAULT_TASK_STATUS: TaskStatusFilter = 'all';

export function isTaskStatusFilter(value: unknown): value is TaskStatusFilter {
  return TASK_STATUS_FILTERS.some((filter) => filter.value === value);
}

/** The instant a task must have closed after to be inside the range. */
function rangeStart(range: TaskRange, now = Date.now()): number {
  if (range === 'today') return new Date(now).setHours(0, 0, 0, 0);
  return now - (range === '7d' ? 7 : 30) * 24 * 60 * 60_000;
}

/**
 * When the fleet stopped touching this task. Only terminal states have one.
 *
 * A timestamp that will not parse counts as the beginning of time rather than
 * as `NaN`, because every comparison with `NaN` is false and a row that answers
 * false to both "recent" and "old" is a row on no page at all - which is the
 * whole class of bug this group exists to end.
 */
function closedAt(task: Task): number {
  const parsed = Date.parse(task.finishedAt ?? task.updatedAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** A failure old enough to be history rather than something to decide today. */
const FAILURE_FRESH_MS = 24 * 60 * 60_000;

/**
 * The two sides of that cut-off, which have to be one rule.
 *
 * The Inbox panel lists the fresh ones and counts the old ones, the Tasks page
 * groups both, and the panel's footer links from one to the other. When each of
 * those asked its own question - one reading `updatedAt`, another
 * `finishedAt` - a failure could be counted in both halves or in neither, and
 * "7 older failures" could land on a page showing two. So: one timestamp, one
 * constant, one predicate each way, and the same `closedAt` the `Closed` group
 * filters on.
 */
export function failures24h(tasks: readonly Task[], now = Date.now()): Task[] {
  return tasks
    .filter((task) => task.status === 'failed' && now - closedAt(task) <= FAILURE_FRESH_MS)
    .sort((a, b) => closedAt(a) - closedAt(b));
}

/** Failures the Inbox panel has stopped showing, newest first. */
export function olderFailures(tasks: readonly Task[], now = Date.now()): Task[] {
  return tasks
    .filter((task) => task.status === 'failed' && now - closedAt(task) > FAILURE_FRESH_MS)
    .sort((a, b) => closedAt(b) - closedAt(a));
}

export interface TaskPageGroup {
  key: string;
  label: string;
  tasks: Task[];
  empty: string;
}

/**
 * The Tasks page's groups, which are not the home board's.
 *
 * The old page grouped tasks into waiting / running / queued / done-today and
 * left failures to the home page's 24h block, so a failure older than a day was
 * on no page at all - and a cancelled task was on no page ever, at any age.
 * `Failed` and `Closed` are therefore both additions rather than ports.
 *
 * `Closed` is history: everything done or cancelled, plus failures old enough
 * that nobody is going to retry them today, back as far as `range` reaches. It
 * is the only group a range applies to, because the other four are what the
 * fleet is doing now and now has no window.
 *
 * `status` narrows the tasks before they are grouped, and drops the groups that
 * cannot hold any of them: filtering to failures should not leave four headings
 * each saying that nothing is there.
 */
export function taskPageGroups(
  tasks: readonly Task[],
  options: { now?: number; range?: TaskRange; status?: TaskStatusFilter } = {},
): TaskPageGroup[] {
  const now = options.now ?? Date.now();
  const range = options.range ?? DEFAULT_TASK_RANGE;
  const status = options.status ?? DEFAULT_TASK_STATUS;
  const wanted = TASK_STATUS_FILTERS.find((filter) => filter.value === status)?.statuses ?? null;
  const visible = wanted
    ? tasks.filter((task) => (wanted as readonly string[]).includes(task.status))
    : tasks;

  const since = rangeStart(range, now);
  const closedEmpty =
    range === 'today'
      ? 'Nothing closed today.'
      : `Nothing closed in the last ${TASK_RANGES.find((entry) => entry.value === range)?.label}.`;
  const inGroup = (statuses: readonly Task['status'][]) =>
    visible.filter((task) => statuses.includes(task.status));

  const groups: (TaskPageGroup & { statuses: readonly Task['status'][] })[] = [
    {
      key: 'gate',
      label: 'Waiting on a human',
      statuses: ['review'],
      empty: 'Nothing is waiting on a human.',
      tasks: inGroup(['review']).sort(
        (a, b) =>
          Date.parse(a.reviewEnteredAt ?? a.updatedAt) -
          Date.parse(b.reviewEnteredAt ?? b.updatedAt),
      ),
    },
    {
      key: 'running',
      label: 'Running',
      statuses: ['running', 'reporting'],
      empty: 'Nothing is running right now.',
      tasks: inGroup(['running', 'reporting']),
    },
    {
      key: 'queued',
      label: 'Queued',
      statuses: ['queued', 'offered', 'claimed', 'draft'],
      empty: 'The queue is empty.',
      tasks: inGroup(['queued', 'offered', 'claimed', 'draft']),
    },
    {
      key: 'failed',
      label: 'Failed',
      statuses: ['failed'],
      empty: 'Nothing has failed in the last 24 hours.',
      tasks: inGroup(['failed']).filter((task) => now - closedAt(task) <= FAILURE_FRESH_MS),
    },
    {
      key: 'closed',
      label: 'Closed',
      statuses: ['done', 'cancelled', 'failed'],
      empty: closedEmpty,
      // The range governs the work that is genuinely over. A failure is not:
      // nobody has decided anything about it, it has simply stopped being this
      // morning's business, and if the range applied to it as well then at
      // `today` a task that failed the day before yesterday would be past the
      // 24h Failed cut-off and before the floor here - on no page at all, which
      // is the exact bug this group was added to fix.
      tasks: visible
        .filter((task) =>
          task.status === 'failed'
            ? now - closedAt(task) > FAILURE_FRESH_MS
            : (task.status === 'done' || task.status === 'cancelled') && closedAt(task) >= since,
        )
        .sort((a, b) => closedAt(b) - closedAt(a)),
    },
  ];

  return groups
    .filter(
      (group) =>
        !wanted || group.statuses.some((one) => (wanted as readonly string[]).includes(one)),
    )
    .map(({ statuses: _statuses, ...group }) => group);
}
